import { ChangeEvent } from "react";
import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { PatientEdit, PatientEditState } from "@/Common/Components/Patient/PatientEdit";
import { PatientAdvancedSearchControl } from "@/Common/Components/Search/PatientAdvancedSearchControl";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { IJPatientCreationModel, IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { TelephoneNumberDto } from "@/Common/Models/Portal/MedfarPortal";
import { PatientProfileMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { PortalStatusIcon } from "../PortalStatusIcon";
import { PatientPortalBanner } from "./PatientPortalBanner";

import img_loading from "@/Globals/_images/loading.gif";
import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPortalPatientSync {
    componentId: string;
    onCancelCallback: () => void;
    onRetriggerSearchCallback?: () => void;
    onPatientCreatedCallback?: (individual: Individual, patientContactId: string, patientContactFacetId: string) => void;
    onPatientAttachedCallback?: (individual: Individual, patientContactId: string, patientContactFacetId: string) => void;
    person: Person;
    initialSearchValue?: string;
}

class PortalPatientSyncState {
    public isLoading: boolean;
    public isSearchDeactivatedFilesEnabled: boolean;
    public searchTerm: string;
    public selectedPatientId: string;
    public isInPatientCreateMode: boolean;
    public isCreatingPatient: boolean;
    public isCreateFormValid: boolean;
    public patientCreationModel: IPatientCreationModel;
    public canAttachPatient: boolean;

    constructor(s: PortalPatientSyncState) {
        if (s != null) {
            this.isInPatientCreateMode = s.isInPatientCreateMode;
            this.isLoading = s.isLoading;
            this.isSearchDeactivatedFilesEnabled = s.isSearchDeactivatedFilesEnabled;
            this.searchTerm = s.searchTerm;
            this.selectedPatientId = s.selectedPatientId;
            this.patientCreationModel = s.patientCreationModel;
            this.isCreatingPatient = s.isCreatingPatient;
            this.isCreateFormValid = s.isCreateFormValid;
            this.canAttachPatient = s.canAttachPatient;
        }
        else {
            this.patientCreationModel = Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel());
            this.searchTerm = "";
        }
    }
}

interface PatientCreateResponse {

    PatientContactId: string;
    PatientContactFacetId: string;
    Individual: Individual;
}

class PortalPatientSync extends React.Component<IPortalPatientSync, PortalPatientSyncState> {
    constructor(props: IPortalPatientSync) {
        super(props);

        const initialState = new PortalPatientSyncState(this.state);
        initialState.searchTerm = this.props.initialSearchValue;
        initialState.isLoading = false;
        this.state = initialState;
    }

    public componentDidMount(): void {
        var elem = $("#" + this.props.componentId);
        const cnt = document.body;
        var dragOptions = {
            containment: cnt,
            handle: ".p-header",
            stop: () => {
                elem.draggable("destroy");
                window.setTimeout(() => {
                    elem.draggable(dragOptions);
                }, 80);
            }
        };

        elem.draggable(dragOptions);
    }

    public render(): JSX.Element {
        return (
            <div className="patient-sync-control" id={this.props.componentId} style={{ width: "1000px" }}>
                <div className="pps-header flex-container p-header">
                    <h4>{cacheStore.GetString("PortalPatientSearchAttachFileLabel")}</h4>
                    <span onClick={() => this.props.onCancelCallback()} className="glyphicon glyphicon-remove pointer" />
                </div>
                <div className="pps-main">
                    <div className="flex-container split">

                        <PatientPortalBanner
                            patient={this.props.person.PortalPatient}
                            patientNAMClickCallback={this.onPortalPatientNAMClick.bind(this)}
                            patientNameClickCallback={this.onPortalPatientNameClick.bind(this)}
                            patientPhoneClickCallBack={this.onPortalPatientPhoneClick.bind(this)}
                        />

                        {this.state.isInPatientCreateMode && (
                            <div className="patientEdit-scroll">
                                <h4> {cacheStore.GetString("CreatePatientLabel")}</h4>
                                <PatientEdit
                                    id="patientedit_portalsync"
                                    patientModel={this.state.patientCreationModel}
                                    stateUpdateFunction={this.notifyParentCreateChanged.bind(this)}
                                    hideFooter={true}
                                    disabled={false /* this.props.AppointmentEditModel.get("IsLoadingPatientProfileFromPortal") */}
                                    duplicatePromptLabelName="DuplicateSentencePromptPatientSyncLabel"
                                    duplicateContinueCallback={this.onAttachAccountClick.bind(this)}
                                    cssClass="portal-patient-popup"
                                    duplicatePopupCSS="adjust-margin"
                                />
                            </div>
                        )}

                        {!this.state.isInPatientCreateMode &&
                            this.renderMylePatientSearch()
                        }

                    </div>
                </div>
                {this.state.isInPatientCreateMode && (
                    <div className="pps-footer flex-container">
                        <button onClick={this.onCreatePatientCancelClick.bind(this)} className="btn btn-default push-right-5"><span>{cacheStore.GetString("CancelLabel")}</span></button>
                        <button onClick={this.onCreatePatientClick.bind(this)} disabled={!this.state.isCreateFormValid || this.state.isLoading} className="btn btn-primary"><span>{cacheStore.GetString(this.state.isCreatingPatient ? "PleaseWaitLabel" : "CreateLabel")}</span></button>
                    </div>
                )}
                {!this.state.isInPatientCreateMode && (
                    <div className="pps-footer flex-container">
                        <button onClick={() => this.props.onCancelCallback()} className="btn btn-default push-right-5"><span>{cacheStore.GetString("CancelLabel")}</span></button>
                        {patientStore.CanCreatePatient && (
                            <button disabled={this.state.isLoading} onClick={this.onCreateNewPatientClick.bind(this)} className="btn btn-primary push-right-5">
                                <span>{cacheStore.GetString("CreatePatientLabel")}</span>
                            </button>
                        )}
                        <button onClick={() => this.onAttachAccountClick()} disabled={!this.state.canAttachPatient || this.state.isLoading} className="btn btn-primary"><span>{cacheStore.GetString("LinkLabel")}</span></button>
                    </div>
                )}
            </div>
        );
    }

    private renderMylePatientSearch(): JSX.Element {
        const self = this;
        const isLoading = this.state.isLoading;

        return (
            <div className="portal-search-panel">
                <div className="ps-pad">
                    <div className="search-component flex-container form-control">
                        <span className="glyphicon glyphicon-search" />
                        <input type="text" disabled={isLoading} value={this.state.searchTerm} onChange={this.onSearchTextChanged.bind(this)} placeholder={cacheStore.GetString("SearchLabel")} />
                    </div>
                </div>
                <div className="search-options flex-container">
                    <div className="checkbox">
                        <label>
                            <input disabled={isLoading} checked={this.state.isSearchDeactivatedFilesEnabled} type="checkbox" onChange={this.onChkDeactivatedEnabledChanged.bind(this)} />
                            {cacheStore.GetString("DeactivatedFilesLabel")}
                        </label>
                    </div>
                </div>
                <div className="portal-patients-results">

                    <PatientAdvancedSearchControl
                        defaultTextValue={self.state.searchTerm}
                        isFocusOnLoad={false}
                        componentId="_patientSync_mylepatientsearch"
                        maxResultsToReturn={8}
                        onPatientSelectCallback={self.onSelectSearchedPatient.bind(self)}
                        isRenderTextbox={false}
                        isScrollBarActive={false}
                        isClickOnResultDisabled={false}
                        isAutoResize={false}
                        isIncludingInactivePatients={self.state.isSearchDeactivatedFilesEnabled}
                        customMylePatientRenderFunction={self.renderWithMylePatientInfo.bind(self)}
                    />

                    {isLoading &&

                        <img src={img_loading} style={{ marginTop: "27%", marginLeft: "46%" }} alt="loading" />
                    }
                </div>
            </div>
        );
    }

    private renderWithMylePatientInfo(p: Person, patientDivClassName: string, idx: number, patientSelectFunction: (p: Person) => void): JSX.Element {
        const highlightNam = p.NAM == this.props.person.NAM;
        const highlightDOB = p.DateofBirth != null && this.props.person.PortalPatient.DateOfBirth && moment(p.DateofBirth).format("YYYYMMDD") == moment(this.props.person.PortalPatient.DateOfBirth).format("YYYYMMDD");
        const highlightAddress = p.GetFormattedAddress() == this.props.person.GetFormattedAddress();
        const highlightFirstName = p.FirstName.toUpperCase() == (this.props.person.FirstName?.toUpperCase() ?? "");
        const highlightLastName = p.LastName.toUpperCase() == (this.props.person.LastName?.toUpperCase() ?? "");
        const hlClass = "highlight-yellow";

        const phone1 = p.Patient.GetFormattedPhonesByPriorityWithPosition(0);
        const phone2 = p.Patient.GetFormattedPhonesByPriorityWithPosition(1);
        const phone3 = p.Patient.GetFormattedPhonesByPriorityWithPosition(2);

        const highlightPhone1 = this.isPhoneMatch(p.Patient.GetPhoneRaw(0), this.props.person.PortalPatient.TelephoneNumbers);
        const highlightPhone2 = this.isPhoneMatch(p.Patient.GetPhoneRaw(1), this.props.person.PortalPatient.TelephoneNumbers);
        const highlightPhone3 = this.isPhoneMatch(p.Patient.GetPhoneRaw(2), this.props.person.PortalPatient.TelephoneNumbers);

        const address = p.Patient.GetFormattedAddress();
        const nextApp = p.Patient.GetNextAppointmentFormatted();
        const isMale = p.Patient.Gender() === Gender.Male;
        const isFemale = p.Patient.Gender() === Gender.Female;

        var patientInfo = (
            <ul>
                {phone1 != "" && <li><span className="myle-icon-16 phone" /><span className={highlightPhone1 ? "content-text " + hlClass : "content-text"}>{phone1}</span></li>}
                {phone2 != "" && <li><span className="myle-icon-16 phone" /><span className={highlightPhone2 ? "content-text " + hlClass : "content-text"}>{phone2}</span></li>}
                {phone3 != "" && <li><span className="myle-icon-16 phone" /><span className={highlightPhone3 ? "content-text " + hlClass : "content-text"}>{phone3}</span></li>}
                {address != "" && <li><span className="myle-icon-16 pin" /><span className={highlightAddress ? "content-text " + hlClass : "content-text"}>{address}</span></li>}
                {nextApp != "" && <li><span className="myle-icon-16 none" /><span className="content-text">{nextApp}</span></li>}
            </ul>
        );

        var portalButton: JSX.Element = null;

        if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
            const portalButtonProps = PortalHelper.getPortalButtonPropsForRegistrationState(p.PortalSyncState);

            portalButton = portalButtonProps && (
                <div>
                    <PortalStatusIcon
                        className="push-right-5"
                        {...portalButtonProps}
                    />
                </div>
            );
        }

        return (
            <div className={patientDivClassName} onClick={() => patientSelectFunction(p)} key={p.ContactId + "_" + idx}>

                <div className="flex-col r1">
                    <img title={p.NameWithIdentifier} src={img_p_banner_icon_100} style={{ width: "48px" }} alt={p.NameWithIdentifier} />
                    {portalButton}
                </div>
                <div style={{ width: "5px" }} />

                <div className="flex-col r2">
                    <h4 className="light-blue"><span className={highlightLastName ? hlClass : ""}> {p.LastName}</span>, <span className={highlightFirstName ? hlClass : ""}>{p.FirstName}</span>
                        {isMale && <span className="myle-icon-16 male" />}
                        {isFemale && <span className="myle-icon-16 female" />}
                    </h4>
                    <ul>
                        <li><strong>{sessionStore.getDefaultHealthCardLabel()} </strong><span className={highlightNam ? hlClass : ""}>{p.GetFormattedNAM()} {p.Patient.GetFormattedNAMExpirationDateString()}</span></li>
                        <li><strong>{cacheStore.GetString("FileNumbersLabel")} </strong><span>{p.Patient.PatientResponse.Identifier01}</span></li>
                        <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span className={highlightDOB ? hlClass : ""}>{p.Patient.GetFormattedDateOfBirth() + p.Patient.GetFormattedAge()}</span></li>
                        <li><strong>{cacheStore.GetString("PhysicianNameLabel")} </strong><span>{p.Patient.GetFormattedPhysicianName()}</span></li>
                    </ul>
                </div>

                <div className="flex-col r3">
                    {patientInfo}
                </div>

            </div>
        );
    }

    private isPhoneMatch(phone: string, phones: TelephoneNumberDto[]): boolean {
        if (phones != null && phones.length > 0) {
            for (let cur of phones) {
                if (cur.Number == phone)
                    return true;
            }
        }
        return false;
    }

    private onServerCallError(error: any): void {
        console.log(error);
    }

    private redirectToPatientProfile(contactId: string): void {
        const redirectOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);
        redirectOperation.AddItem("value", contactId);
        redirectOperation.AddItem("target", RedirectTo.Patient);
        redirectOperation.AddItem("currentLocation", RouterHistory.location);
        LocalEventDispatcher.dispatch(redirectOperation);
    }

    // ----------------------------------------------------------------------------------------------------
    // ------------------------------- search and attach --------------------------------------------------
    // ----------------------------------------------------------------------------------------------------

    private onPortalPatientNameClick(portalPatient: PatientProfileMyleDto): void {
        const self = this;
        const s = new PortalPatientSyncState(this.state);
        s.searchTerm = portalPatient.LastName + " " + portalPatient.FirstName;
        self.setState(s);
    }

    private onPortalPatientNAMClick(portalPatient: PatientProfileMyleDto): void {
        const self = this;
        const s = new PortalPatientSyncState(this.state);
        s.searchTerm = portalPatient.InsuranceNumber;
        self.setState(s);
    }

    private onPortalPatientPhoneClick(portalPatient: PatientProfileMyleDto, phone: TelephoneNumberDto): void {
        const self = this;
        const s = new PortalPatientSyncState(this.state);
        const phoneCountryLookupCode = cacheStore.GetCountryLookupCodeByISOCodeAlpha2(phone.CountryCode);
        const phoneLookup = cacheStore.FindLookupByLookupCode(phoneCountryLookupCode);
        let phoneCountryLookupId = null;
        if (phoneLookup != null) phoneCountryLookupId = phoneLookup.LookupID;
        s.searchTerm = ValidationHelper.cleanPhoneNumber(phone.Number, phoneCountryLookupId);
        self.setState(s);
    }

    private onSearchTextChanged(e: ChangeEvent<HTMLInputElement>): void {
        const self = this;
        const txt = e.target.value;
        const s = new PortalPatientSyncState(this.state);
        s.searchTerm = txt;
        self.setState(s);
    }

    private onChkDeactivatedEnabledChanged(e: ChangeEvent<HTMLInputElement>): void {
        const self = this;
        const checked = e.target.checked;
        const s = new PortalPatientSyncState(this.state);
        s.isSearchDeactivatedFilesEnabled = checked;
        self.setState(s);
    }

    private onSelectSearchedPatient(selectedPerson: Person): void {
        const s = new PortalPatientSyncState(this.state);
        s.selectedPatientId = selectedPerson.ContactId;

        if (selectedPerson.PortalProfileId != null && selectedPerson.PortalProfileId.length > 0)
            s.canAttachPatient = false;
        else
            s.canAttachPatient = true;

        this.setState(s);
    }

    private onAttachAccountClick(selectedPersonAccount?: Person): void {
        const client = new Client();
        const self = this;

        const requestData = {
            mylePatientId: selectedPersonAccount?.ContactId || self.state.selectedPatientId,
            portalPatientId: self.props.person.PortalPatient.PatientProfileId
        };

        client.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_LinkMylePatient, requestData, self.onAttachAccountResponse.bind(self), self.onServerCallError.bind(self));
    }

    private onAttachAccountResponse(response: { Success: boolean, Individual: Individual, PatientInactivableId: string, PatientContactFacetId: string }): void {
        const self = this;
        const success = response.Success as boolean;
        if (success) {
            if (self.props.onPatientAttachedCallback) {
                self.props.onPatientAttachedCallback(response.Individual, response.PatientInactivableId, response.PatientContactFacetId);
            }
            else {
                const op = new LocalEvent(LocalOperations.MainComponentViewOperations.HideSyncModal);
                MainComponentStateDispatcher.dispatch(op);
                window.setTimeout(() => {
                    self.retriggerSideBarSearch();
                    self.redirectToPatientProfile(self.state.selectedPatientId);
                }, 100);
            }
        }
    }

    // ----------------------------------------------------------------------------------------------------
    // ------------------------------- create new patient--------------------------------------------------
    // ----------------------------------------------------------------------------------------------------

    private onCreateNewPatientClick(): void {
        const s = new PortalPatientSyncState(this.state);

        if (this.props.person != null && this.props.person.PortalPatient != null)
            s.patientCreationModel = PatientHelper.getPatientCreationModelFromPortalPatient(this.props.person.PortalPatient);
        else
            s.patientCreationModel = Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel());

        s.isInPatientCreateMode = true;
        this.setState(s);
    }

    private onCreatePatientClick(): void {
        const client = new Client();
        const patient = this.state.patientCreationModel;

        var requestData = {
            SelectedPrefixLookupId: patient.get("SelectedPrefixLookupId"),
            FirstName: patient.get("FirstName"),
            LastName: patient.get("LastName"),
            PreferredName: patient.get("PreferredName"),
            PreferredPronouns: patient.get("PreferredPronouns"),
            SpecifiedPreferredPronouns: patient.get("SpecifiedPreferredPronouns"),
            GenderLookupId: patient.get("GenderLookupId"),
            GenderIdentity: patient.get("GenderIdentity"),
            DateOfBirth: patient.get("DOB"),
            CivicAddress: patient.get("CivicAddressEdit"),
            City: patient.get("CityEdit"),
            PostalCode: patient.get("PostalCodeEdit"),
            NAM: patient.get("NAMEdit"),
            HealthCardRegionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(patient),
            NAMExpiry: patient.get("NAMExpiryEdit"),
            CountryLookupId: patient.get("SelectedCountryLookupId"),
            RegionLookupId: patient.get("SelectedRegionLookupId"),
            Phone1Number: patient.get("Phone1Edit"),
            Phone1ContactTypeLookupId: patient.get("Phone1ContactTypeLookupId"),
            Phone1TypeLookupId: patient.get("Phone1TypeLookupId"),
            Phone1Ext: patient.get("Phone1Extension"),
            Phone1NotificationsEnabled: patient.get("Phone1NotificationsEnabled"),
            Phone2Number: patient.get("Phone2Edit"),
            Phone2ContactTypeLookupId: patient.get("Phone2ContactTypeLookupId"),
            Phone2TypeLookupId: patient.get("Phone2TypeLookupId"),
            Phone2Ext: patient.get("Phone2Extension"),
            Phone2NotificationsEnabled: patient.get("Phone2NotificationsEnabled"),
            EmailAddress: patient.get("EmailEdit"),
            EmailTypeByLookup: patient.get("EmailTypeByLookup"),
            IsOkToContact: patient.get("IsOkToContact"),
            EmailNotificationsEnabled: patient.get("EmailNotificationsEnabled"),
            PortalProfileId: this.props.person.PortalPatient.PatientProfileId,
            PreferredSpokenLanguageLookupId: patient.get("PreferredSpokenLanguageLookupId"),
            MotherLastName: patient.get("motherLastName"),
            MotherFirstName: patient.get("motherFirstName")
        };

        const s = new PortalPatientSyncState(this.state);
        s.isLoading = true;

        this.setState(
            s,
            () => client.Post(
                apiSettings.buildApiPath(),
                ServerOperations.Server.Myle_CreatePatient,
                requestData,
                response => this.onCreatePatientResponse(response),
                error => this.onServerCallError(error)
            )
        );
    }

    private onCreatePatientResponse(response: PatientCreateResponse): void {
        const self = this;

        const s = new PortalPatientSyncState(self.state);
        s.isLoading = false;
        self.setState(s);

        if (self.props.onPatientCreatedCallback) {
            self.props.onPatientCreatedCallback(response.Individual, response.PatientContactId, response.PatientContactFacetId);
        }
        else {
            // hide current popup
            const op = new LocalEvent(LocalOperations.MainComponentViewOperations.HideSyncModal);
            MainComponentStateDispatcher.dispatch(op);

            window.setTimeout(() => {
                self.retriggerSideBarSearch();
                self.redirectToPatientProfile(response.Individual.Contact_id);
            }, 100);
        }
    }

    private onCreatePatientCancelClick(): void {
        const s = new PortalPatientSyncState(this.state);
        s.isInPatientCreateMode = false;
        s.isLoading = false;
        this.setState(s);
    }

    private notifyParentCreateChanged(newPatientCreateState: Readonly<PatientEditState>): void {
        const s = new PortalPatientSyncState(this.state);
        s.isCreateFormValid = PatientEditState.IsValid(newPatientCreateState);
        s.patientCreationModel = newPatientCreateState.patientModel;
        this.setState(s);
    }

    private retriggerSideBarSearch(): void {
        window.setTimeout(() => {
            const op2 = new LocalEvent(LocalOperations.Sidebar.SearchTextChanged);
            op2.AddItem("value", sidebarStore.Model.get("SearchText")); // if sidebar
            LocalEventDispatcher.dispatch(op2);
        }, 50);
    }
}

export { PortalPatientSync };
